import styled from '@emotion/styled'
import { useEffect, useRef, useState } from 'react'
import { MenuItem, Divider } from '@mui/material'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { tokens } from '../../theme.js'
import { errorCode } from '../../api/errors.js'
import { useExclusionNoteLimit } from '../../hooks/useLimits.js'
import { formatSize, formatDay, join, sizeOf, TIER_LABEL } from '../common/text.js'
import {
  TableCheckbox,
  KindBadge,
  StatusPill,
  IconOnlyButton,
  IconButton,
  SecondaryIconButton,
  MutedText,
  ErrorText,
  ActionMenu,
  plural
} from '../styles/index.js'
import { FILE_IDS_TYPE, FOLDER_IDS_TYPE } from './FolderTree.js'
import { RENAME_ERRORS } from '../common/errorCopy.js'
import { STATUS_LABEL, pdfLike, convertible, displayKind, originalDisplayKind } from './fileStatus.js'
import { SORT_COLUMNS, sortLabel } from './tableView.js'

const colors = tokens.colors
const fontFamily = tokens.fonts.body

const extensionOf = name => /^.+(\.[^.]+)$/.exec(name)?.[1] ?? ''

export const DOWNLOADS = [
  { artifact: 'original', label: 'Original', has: () => true, meta: f => join(originalDisplayKind(f), sizeOf(f.sizeBytes)) },
  {
    artifact: 'pdf',
    label: 'PDF',
    has: f => !!f.pdfObjectKey,
    after: 'after Convert',
    meta: f =>
      f.pdfObjectKey === f.objectKey
        ? `= original${f.kind === 'PDF' ? ' (PDF upload)' : ''}`
        : join(f.pageCount ? plural(f.pageCount, 'page') : '', sizeOf(f.artifacts?.converted?.sizeBytes))
  },
  {
    artifact: 'annotated',
    label: 'Annotated',
    has: f => !!f.artifacts?.annotated,
    after: 'after Apply',
    meta: f => sizeOf(f.artifacts?.annotated?.sizeBytes)
  },
  {
    artifact: 'redacted',
    label: 'Redacted',
    has: f => !!f.artifacts?.redacted,
    after: 'after Apply',
    meta: f => sizeOf(f.artifacts?.redacted?.sizeBytes)
  },
  {
    artifact: 'reasons',
    label: 'Summary',
    has: f => !!f.artifacts?.reasons,
    after: 'after Apply',
    meta: f => sizeOf(f.artifacts?.reasons?.sizeBytes)
  }
]

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  & th {
    text-align: left;
    font: 500 13px/1.3 ${fontFamily};
    color: ${colors.text.muted};
    height: 40px;
    padding: 0 8px;
    border-bottom: 1px solid ${colors.sectionDivider};
  }
  & td {
    font: 400 14px/1.3 ${fontFamily};
    color: ${colors.text.body};
    padding: 0 8px;
    height: 48px;
    border-bottom: 1px solid ${colors.background.medium};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  & th.num,
  & td.num {
    text-align: right;
  }
  & td.num {
    color: ${colors.text.body};
  }
  & td.handle {
    padding: 0;
    text-align: center;
    overflow: visible;
  }
`
const Tr = styled.tr`
  user-select: none;
  background: ${p => (p.$selected ? colors.primaryExtraLight : p.$editing ? colors.white : 'transparent')};
  &:hover {
    background: ${p => (p.$selected ? colors.primaryExtraLight : p.$editing ? colors.white : colors.background.light)};
  }
  ${p => (p.$hovered ? `background: ${p.$selected ? colors.primaryExtraLight : p.$editing ? colors.white : colors.background.light};` : '')}
  ${p => (p.$suppressHover ? `&:hover { background: ${p.$selected ? colors.primaryExtraLight : p.$editing ? colors.white : 'transparent'}; }` : '')}
  ${p => (p.$lifted ? 'opacity: 0.4;' : '')}
  ${p => (p.$line === 'top' ? `& > td { box-shadow: inset 0 2px 0 0 ${colors.primary}; }` : '')}
  ${p => (p.$line === 'bottom' ? `& > td { border-bottom-color: ${colors.primary}; box-shadow: inset 0 -1px 0 0 ${colors.primary}; }` : '')}
`
const FolderTr = styled(Tr)`
  cursor: pointer;
  ${p =>
    p.$drop
      ? `background: ${colors.primaryLight}; &:hover { background: ${colors.primaryLight}; }
    & > td { box-shadow: inset 0 1.5px 0 0 ${colors.primary}, inset 0 -1.5px 0 0 ${colors.primary}; }`
      : ''}
`
const FolderName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`
const Footer = styled.p`
  margin: 1px 0 0;
  font: 400 12px/1.4 ${fontFamily};
  color: ${colors.text.muted};
`
const Ghost = styled.div`
  position: fixed;
  z-index: 1500;
  pointer-events: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px 8px 10px;
  border-radius: 20px;
  background: ${colors.white};
  border: 1.5px solid ${colors.primary};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  font: 500 13px/1 ${fontFamily};
  color: ${colors.text.body};
  white-space: nowrap;
  & > .MuiSvgIcon-root {
    font-size: 18px;
    color: ${colors.text.body}BF;
  }
`
const GhostCount = styled.span`
  background: ${colors.primary};
  color: ${colors.white};
  border-radius: 10px;
  padding: 3px 8px;
  font: 600 11px/1 ${fontFamily};
`
const Handle = styled.span`
  display: inline-flex;
  color: ${colors.text.muted};
  cursor: grab;
  vertical-align: middle;
  & svg {
    pointer-events: none;
  }
`
const FileName = styled.span`
  display: inline-flex;
  max-width: 100%;
  vertical-align: middle;
  font: 500 14px/1.3 ${fontFamily};
  cursor: ${p => (p.$link ? 'pointer' : 'default')};
  &:hover {
    color: ${p => (p.$link ? colors.primary : colors.text.body)};
  }
  & > .base {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  & > .suffix {
    flex: 0 0 auto;
    white-space: pre;
  }
`
function Name({ name }) {
  const at = name.lastIndexOf(' — ')
  if (at < 0) return <span className="base">{name}</span>
  return (
    <>
      <span className="base">{name.slice(0, at)}</span>
      <span className="suffix">{name.slice(at)}</span>
    </>
  )
}
const PillButton = styled.button`
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  & > div:hover {
    filter: brightness(0.96);
  }
`
const Submenu = styled.span`
  margin-left: auto;
  display: inline-flex;
  & .MuiSvgIcon-root {
    font-size: 16px;
  }
`
const ItemMeta = styled.span`
  margin-left: auto;
  padding-left: 24px;
  font: 400 12px/1.4 ${fontFamily};
  color: ${colors.text.muted};
`

const NUM_COLUMNS = new Set(['pages', 'size'])
const HeadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font: 400 13px/1.3 ${fontFamily};
  color: ${p => (p.$on ? colors.primary : colors.text.muted)};
  &:hover {
    color: ${colors.primary};
  }
  & .MuiSvgIcon-root {
    font-size: 14px;
  }
`

const Editor = styled.div`
  padding: 6px 12px 6px 0;
  user-select: text;
`
const EditorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`
const StemInput = styled.input`
  flex: 1;
  min-width: 60px;
  max-width: 280px;
  height: 28px;
  padding: 0 10px;
  font: 400 14px ${fontFamily};
  color: ${colors.inputField.text};
  background: ${colors.inputField.background};
  border: 1px solid ${p => (p.$error ? colors.error : colors.inputField.focusedBorder)};
  border-radius: ${tokens.borderRadius.input};
  &:focus {
    outline: none;
  }
  &::selection {
    background: ${colors.primaryExtraLight};
  }
`
const SquareButton = { padding: '3px', width: 26, height: 26 }
const NoteCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  & > button {
    margin-left: auto;
  }
`
const NoteInput = styled(StemInput)`
  max-width: none;
  width: 100%;
`
const renameError = e => RENAME_ERRORS[errorCode(e)] || (e.response ? "Couldn't rename the file." : 'No connection. Try again.')

function RenameEditor({ file, onSave, onCancel }) {
  const ext = extensionOf(file.name)
  const [stem, setStem] = useState(file.name.slice(0, file.name.length - ext.length))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const name = stem.trim() + ext
  const canSave = stem.trim().length > 0 && !busy

  async function save() {
    if (!canSave) return
    if (name === file.name) return onCancel()
    setBusy(true)
    try {
      await onSave(name)
    } catch (e) {
      setError(renameError(e))
      setBusy(false)
    }
  }
  const onKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      save()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      if (!busy) onCancel()
    }
  }
  return (
    <Editor data-testid="rename-editor" onClick={e => e.stopPropagation()} onKeyDown={onKeyDown}>
      <EditorRow>
        <StemInput
          autoFocus
          value={stem}
          onChange={e => {
            setStem(e.target.value)
            setError('')
          }}
          onFocus={e => e.target.select()}
          disabled={busy}
          $error={!!error}
          aria-label="File name"
          data-testid="rename-input"
        />
        {ext && (
          <MutedText style={{ fontSize: 14 }} data-testid="rename-ext">
            {ext}
          </MutedText>
        )}
        <IconButton type="button" style={SquareButton} onClick={save} disabled={!canSave} title="Save (Enter)" data-testid="rename-save">
          <CheckIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <SecondaryIconButton type="button" style={SquareButton} onClick={onCancel} disabled={busy} title="Cancel (Esc)" data-testid="rename-cancel">
          <CloseIcon sx={{ fontSize: 18 }} />
        </SecondaryIconButton>
        <KindBadge
          kind={displayKind(file)}
          title={file.pdfObjectKey && file.kind !== 'PDF' && file.kind !== 'COMBINED' ? `Original format: ${file.kind}` : undefined}
          style={{ marginLeft: 6 }}
        />
      </EditorRow>
      {error && (
        <ErrorText role="alert" data-testid="rename-error">
          {error}
        </ErrorText>
      )}
    </Editor>
  )
}

function NoteEditor({ file, onSave, onCancel }) {
  const noteMax = useExclusionNoteLimit()
  const [text, setText] = useState(file.excludedNote ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function save() {
    if (busy) return
    if (text.trim() === (file.excludedNote ?? '')) return onCancel()
    setBusy(true)
    try {
      await onSave(text.trim())
    } catch (e) {
      setError(e.response ? "Couldn't save the note." : 'No connection. Try again.')
      setBusy(false)
    }
  }
  return (
    <div
      onClick={e => e.stopPropagation()}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault()
          save()
        } else if (e.key === 'Escape') {
          e.preventDefault()
          if (!busy) onCancel()
        }
      }}
      data-testid="note-editor">
      <EditorRow>
        <NoteInput
          autoFocus
          value={text}
          maxLength={noteMax}
          onChange={e => {
            setText(e.target.value)
            setError('')
          }}
          disabled={busy}
          $error={!!error}
          aria-label="Exclusion note"
          data-testid="note-input"
        />
        <IconButton type="button" style={SquareButton} onClick={save} disabled={busy} title="Save (Enter)" data-testid="note-save">
          <CheckIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <IconOnlyButton
          type="button"
          style={{ ...SquareButton, color: colors.text.muted, background: colors.white, border: `1px solid ${colors.background.medium}` }}
          onClick={onCancel}
          disabled={busy}
          title="Cancel (Esc)"
          data-testid="note-cancel">
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconOnlyButton>
      </EditorRow>
      {error && (
        <ErrorText role="alert" data-testid="note-error">
          {error}
        </ErrorText>
      )}
    </div>
  )
}

const orderCaption = priority => `${priority.map(t => TIER_LABEL[t] || t).join(' → ')} → upload`

function statusOf(f, running) {
  if (running) return { status: 'PROCESSING', label: running }
  const label = STATUS_LABEL[f.status] || f.status
  if (f.status === 'SUGGESTED') return { status: f.status, label, opens: 'editor', note: `${plural(f.redactionCount ?? 0, 'box')} · not applied yet` }
  if (f.status === 'REDACTED')
    return { status: f.status, label, opens: 'redacted', note: f.artifacts?.redacted?.at ? `applied ${formatDay(f.artifacts.redacted.at)}` : '' }
  return { status: f.status, label }
}

let blank
function blankDragImage() {
  if (!blank) {
    blank = document.createElement('canvas')
    blank.width = 1
    blank.height = 1
    blank.style.position = 'fixed'
    blank.style.left = '-10000px'
    blank.style.top = '-10000px'
    blank.setAttribute('aria-hidden', 'true')
    document.body.appendChild(blank)
  }
  return blank
}

export function FileTable({
  files,
  folders = [],
  selection,
  running = {},
  resetKey,
  view = 'files',
  onOpen,
  onDownload,
  onConvert,
  onRename,
  onMove,
  onExclude,
  onRestore,
  onNote,
  onDelete,
  onUncombine,
  onExportAnnotated,
  onImportAnnotated,
  onReorder,
  orderManual = false,
  priority = ['filename', 'document', 'metadata'],
  containerLabel = 'this folder',
  countLabel,
  sort = null,
  onSort,
  hiddenCount = 0,
  onOpenFolder,
  onRenameFolder,
  onMoveFolder,
  onDeleteFolder,
  onDropOnFolder,
  onDropFolder,
  canDropFolder = () => true
}) {
  const excludedView = view === 'excluded'
  const noteMax = useExclusionNoteLimit()
  const [menu, setMenu] = useState(null) // { el, file }
  const [folderMenu, setFolderMenu] = useState(null) // { el, folder }
  const [folderDrop, setFolderDrop] = useState(null) // folder id under a file/folder drag
  const [folderDrag, setFolderDrag] = useState(null)
  const [downloads, setDownloads] = useState(null) // the Download submenu's anchor
  const [renaming, setRenaming] = useState(null) // file id under inline rename
  const [noting, setNoting] = useState(null) // file id under inline note editing (Excluded)
  const [drag, setDrag] = useState(null) // { name, ids, x, y } while rows drag
  const [over, setOver] = useState(null) // { id, edge: 'before' | 'after' } the drop slot
  const [hoveredFileId, setHoveredFileId] = useState(null)
  const dragRef = useRef(null)
  useEffect(() => {
    setRenaming(null)
    setNoting(null)
  }, [resetKey])
  useEffect(() => {
    if (renaming && !files.some(f => f.id === renaming)) setRenaming(null)
  }, [files, renaming])
  useEffect(() => {
    if (noting && !files.some(f => f.id === noting)) setNoting(null)
  }, [files, noting])
  useEffect(() => {
    if (hoveredFileId && !files.some(f => f.id === hoveredFileId)) setHoveredFileId(null)
  }, [files, hoveredFileId])
  useEffect(() => {
    if (!drag) return undefined
    const move = e => setDrag(d => (d ? { ...d, x: e.clientX, y: e.clientY } : d))
    document.addEventListener('dragover', move)
    return () => document.removeEventListener('dragover', move)
  }, [!!drag])
  useEffect(() => {
    const clear = () => {
      dragRef.current = null
      setDrag(null)
      setOver(null)
      setFolderDrop(null)
      setFolderDrag(null)
    }
    document.addEventListener('dragend', clear)
    document.addEventListener('drop', clear)
    return () => {
      document.removeEventListener('dragend', clear)
      document.removeEventListener('drop', clear)
    }
  }, [])
  const editorSpan = 5 // name, file type, pages, size, date
  const reorderable = !sort && hiddenCount === 0
  const baseOrder = orderManual ? `manual (${containerLabel})` : `document date (${orderCaption(priority)}), then name`
  const allIds = files.map(f => f.id)
  const selectableIds = [...folders.map(g => g.id), ...allIds]
  const all = selection.allSelected(selectableIds)
  const some = !all && selection.someSelected(selectableIds)

  function startDrag(e, f) {
    if (e.target.closest?.('button, input, a, img')) {
      e.preventDefault()
      return
    }
    const ids = selection.isSelected(f.id) ? allIds.filter(id => selection.isSelected(id)) : [f.id]
    e.dataTransfer.setData(FILE_IDS_TYPE, JSON.stringify(ids))
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setDragImage(blankDragImage(), 0, 0)
    dragRef.current = ids
    setHoveredFileId(f.id)
    setDrag({ name: f.name, ids, x: e.clientX, y: e.clientY })
  }
  function endDrag() {
    dragRef.current = null
    setDrag(null)
    setOver(null)
  }
  function dragOverRow(e, f) {
    if (!reorderable || !dragRef.current || !e.dataTransfer.types.includes(FILE_IDS_TYPE)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setHoveredFileId(f.id)
    const box = e.currentTarget.getBoundingClientRect()
    const edge = e.clientY < box.top + box.height / 2 ? 'before' : 'after'
    if (dragRef.current.includes(f.id)) {
      if (over) setOver(null)
      return
    }
    if (over?.id !== f.id || over?.edge !== edge) setOver({ id: f.id, edge })
  }
  function dropOnRow(e, f) {
    if (!reorderable || !dragRef.current) return
    e.preventDefault()
    const lifted = new Set(dragRef.current)
    const movedFileId = dragRef.current[0]
    const rest = allIds.filter(id => !lifted.has(id))
    let at = rest.indexOf(f.id)
    if (at < 0) return endDrag()
    const box = e.currentTarget.getBoundingClientRect()
    if (over?.id === f.id ? over.edge === 'after' : e.clientY >= box.top + box.height / 2) at += 1
    const next = [...rest.slice(0, at), ...allIds.filter(id => lifted.has(id)), ...rest.slice(at)]
    endDrag()
    setHoveredFileId(movedFileId)
    if (next.some((id, i) => id !== allIds[i])) onReorder?.(next)
  }
  const closeMenus = () => {
    setDownloads(null)
    setMenu(null)
  }
  const act = fn => () => {
    const f = menu.file
    closeMenus()
    fn(f)
  }
  const folderAct = fn => () => {
    const g = folderMenu.folder
    setFolderMenu(null)
    fn(g)
  }
  const folderDropProps = g => ({
    onDragOver: e => {
      if (e.dataTransfer.types.includes(FILE_IDS_TYPE)) {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        if (folderDrop !== g.id) setFolderDrop(g.id)
        if (over) setOver(null)
      } else if (e.dataTransfer.types.includes(FOLDER_IDS_TYPE)) {
        let ids = []
        try {
          ids = JSON.parse(e.dataTransfer.getData(FOLDER_IDS_TYPE) || '[]')
        } catch {
          ids = []
        }
        if (!canDropFolder(ids, g.id)) {
          setFolderDrop(null)
          return
        }
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        if (folderDrop !== g.id) setFolderDrop(g.id)
        if (over) setOver(null)
      }
    },
    onDragEnter: e => {
      if (e.dataTransfer.types.includes(FILE_IDS_TYPE) || e.dataTransfer.types.includes(FOLDER_IDS_TYPE)) e.preventDefault()
    },
    onDragLeave: e => {
      if (!e.currentTarget.contains(e.relatedTarget)) setFolderDrop(d => (d === g.id ? null : d))
    },
    onDrop: e => {
      e.preventDefault()
      setFolderDrop(null)
      let ids = []
      try {
        ids = JSON.parse(e.dataTransfer.getData(FILE_IDS_TYPE) || '[]')
      } catch {
        ids = []
      }
      endDrag()
      if (ids.length) onDropOnFolder?.(ids, g.id)
      let folderIds = []
      try {
        folderIds = JSON.parse(e.dataTransfer.getData(FOLDER_IDS_TYPE) || '[]')
      } catch {
        folderIds = []
      }
      if (folderIds.length && canDropFolder(folderIds, g.id)) onDropFolder?.(folderIds, g.id)
    }
  })
  const startFolderDrag = (e, g) => {
    if (e.target.closest?.('button, input, a, img')) {
      e.preventDefault()
      return
    }
    const ids = selection.isSelected(g.id) ? folders.filter(folder => selection.isSelected(folder.id)).map(folder => folder.id) : [g.id]
    e.dataTransfer.setData(FOLDER_IDS_TYPE, JSON.stringify(ids))
    e.dataTransfer.effectAllowed = 'move'
    setFolderDrag(g.id)
  }
  const dropOf = f => (over?.id === f.id ? over.edge : undefined)
  const overIndex = over ? allIds.indexOf(over.id) : -1
  const lineIndex = over ? (over.edge === 'after' ? overIndex : overIndex - 1) : null
  const lineOf = i => (lineIndex === null ? undefined : lineIndex === i ? 'bottom' : lineIndex === -1 && i === 0 ? 'top' : undefined)

  const downloadMenu = menu && (
    <>
      <MenuItem onClick={e => setDownloads(e.currentTarget)} data-testid="download-menu" aria-haspopup="menu" aria-expanded={!!downloads}>
        <DownloadOutlinedIcon /> Download
        <Submenu>
          <ChevronRightIcon />
        </Submenu>
      </MenuItem>
      <ActionMenu
        anchorEl={downloads}
        open={!!downloads}
        onClose={() => setDownloads(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {DOWNLOADS.map(d => {
          const present = d.has(menu.file)
          return (
            <MenuItem
              key={d.artifact}
              disabled={!present}
              onClick={present ? act(f => onDownload(f, d.artifact)) : undefined}
              data-testid={`download-${d.artifact}`}>
              {present ? <DownloadOutlinedIcon /> : <DoNotDisturbAltIcon />} {d.label}
              <ItemMeta data-testid="download-meta">{present ? d.meta(menu.file) : d.after}</ItemMeta>
            </MenuItem>
          )
        })}
      </ActionMenu>
    </>
  )

  if (excludedView) {
    return (
      <>
        <Table data-testid="file-table" data-view="excluded">
          <colgroup>
            <col style={{ width: 36 }} />
            <col />
            <col style={{ width: 112 }} />
            <col style={{ width: 64 }} />
            <col style={{ width: 84 }} />
            <col style={{ width: 130 }} />
            <col style={{ width: 300 }} />
            <col style={{ width: 76 }} />
          </colgroup>
          <thead>
            <tr>
              <th>
                <TableCheckbox
                  checked={all}
                  indeterminate={some}
                  onChange={() => selection.toggleAll(selectableIds)}
                  inputProps={{ 'data-testid': 'select-all', 'data-indeterminate': String(some), 'aria-label': 'select all shown files' }}
                />
              </th>
              <th>Name</th>
              <th>File type</th>
              <th className="num" data-testid="col-pages">
                Pages
              </th>
              <th className="num" data-testid="col-size">
                Size
              </th>
              <th>Excluded on</th>
              <th>Note</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {files.map(f => {
              const selected = selection.isSelected(f.id)
              const editing = noting === f.id
              return (
                <Tr
                  key={f.id}
                  data-testid="file-row"
                  data-file-name={f.name}
                  data-file-id={f.id}
                  data-selected={selected}
                  $selected={selected}
                  $editing={editing}>
                  <td>
                    <TableCheckbox
                      checked={selected}
                      onChange={() => selection.toggle(f.id)}
                      inputProps={{ 'data-testid': 'file-select', 'aria-label': `select ${f.name}` }}
                    />
                  </td>
                  <td title={f.name}>
                    <FileName data-testid="file-name" $link={pdfLike(f)} onClick={() => pdfLike(f) && onOpen(f)}>
                      <Name name={f.name} />
                    </FileName>
                  </td>
                  <td>
                    <KindBadge
                      kind={displayKind(f)}
                      title={f.pdfObjectKey && f.kind !== 'PDF' && f.kind !== 'COMBINED' ? `Original format: ${f.kind}` : undefined}
                      data-testid="file-kind"
                    />
                  </td>
                  <td className="num" data-testid="file-pages">
                    {f.pageCount ?? '—'}
                  </td>
                  <td className="num" data-testid="file-size">
                    {formatSize(f.sizeBytes)}
                  </td>
                  <td data-testid="file-excluded-at">
                    {formatDay(f.excludedAt)}
                    {f.excludedBy && (
                      <MutedText
                        title={f.excludedBy}
                        data-testid="file-excluded-by"
                        style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.excludedBy}
                      </MutedText>
                    )}
                  </td>
                  <td>
                    {editing ? (
                      <NoteEditor
                        file={f}
                        onSave={async note => {
                          await onNote(f, note)
                          setNoting(null)
                        }}
                        onCancel={() => setNoting(null)}
                      />
                    ) : (
                      <NoteCell>
                        <span title={f.excludedNote || undefined} data-testid="file-note">
                          {f.excludedNote || '—'}
                        </span>
                        <IconOnlyButton
                          type="button"
                          onClick={() => setNoting(f.id)}
                          data-testid="edit-note"
                          title={f.excludedNote ? 'Edit the note' : 'Add a note'}
                          aria-label={`edit the note on ${f.name}`}
                          style={{ color: colors.text.muted, padding: 2 }}>
                          <EditOutlinedIcon sx={{ fontSize: 15 }} />
                        </IconOnlyButton>
                      </NoteCell>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                      <SecondaryIconButton type="button" style={SquareButton} onClick={() => onRestore(f)} data-testid="restore-file" title="Restore">
                        <ReplayOutlinedIcon sx={{ fontSize: 16 }} />
                      </SecondaryIconButton>
                      <IconOnlyButton
                        onClick={e => setMenu({ el: e.currentTarget, file: f })}
                        data-testid="file-menu"
                        title="More"
                        style={{ color: colors.text.muted }}>
                        <MoreVertIcon sx={{ fontSize: 18 }} />
                      </IconOnlyButton>
                    </div>
                  </td>
                </Tr>
              )
            })}
          </tbody>
        </Table>
        {files.length > 0 && (
          <Footer data-testid="table-footer" data-order="excluded">
            {files.length === 1 ? '1 excluded file' : `${files.length} excluded files`} · restore returns a file to its original folder at its
            document-date position and clears its note · notes are free text (≤ {noteMax} chars)
          </Footer>
        )}
        <ActionMenu anchorEl={menu?.el} open={!!menu} onClose={closeMenus}>
          {menu && pdfLike(menu.file) && (
            <MenuItem onClick={act(onOpen)} data-testid="open-file">
              <OpenInNewIcon /> Open
            </MenuItem>
          )}
          {downloadMenu}
          <MenuItem onClick={act(f => setNoting(f.id))} data-testid="menu-note">
            <EditOutlinedIcon /> {menu?.file?.excludedNote ? 'Edit note' : 'Add note'}
          </MenuItem>
          <Divider />
          <MenuItem onClick={act(onRestore)} data-testid="menu-restore">
            <ReplayOutlinedIcon /> Restore
          </MenuItem>
        </ActionMenu>
      </>
    )
  }

  return (
    <>
      <Table data-testid="file-table">
        <colgroup>
          <col style={{ width: 36 }} />
          <col style={{ width: 28 }} />
          <col />
          <col style={{ width: 112 }} />
          <col style={{ width: 64 }} />
          <col style={{ width: 84 }} />
          <col style={{ width: 130 }} />
          <col style={{ width: 180 }} />
          <col style={{ width: 76 }} />
        </colgroup>
        <thead>
          <tr>
            <th>
              {selectableIds.length > 0 && (
                <TableCheckbox
                  checked={all}
                  indeterminate={some}
                  onChange={() => selection.toggleAll(selectableIds)}
                  inputProps={{ 'data-testid': 'select-all', 'data-indeterminate': String(some), 'aria-label': 'select all shown files' }}
                />
              )}
            </th>
            <th />
            {SORT_COLUMNS.map(col => {
              const on = sort?.key === col.key
              return (
                <th
                  key={col.key}
                  className={NUM_COLUMNS.has(col.key) ? 'num' : undefined}
                  data-testid={`col-${col.key}`}
                  data-sort={on ? sort.dir : 'none'}>
                  <HeadButton
                    type="button"
                    $on={on}
                    onClick={() => onSort?.(col.key)}
                    title={`Sort by ${col.label}`}
                    data-testid={`sort-${col.key}`}
                    aria-label={`Sort by ${col.label}`}
                    aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                    {on && (sort.dir === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />)}
                    {col.label}
                  </HeadButton>
                </th>
              )
            })}
            <th />
          </tr>
        </thead>
        <tbody>
          {folders.map(g => (
            <FolderTr
              key={g.id}
              data-testid="folder-row"
              data-folder-name={g.name}
              data-folder-id={g.id}
              data-drop={folderDrop === g.id}
              data-selected={selection.isSelected(g.id)}
              data-dragging={folderDrag === g.id}
              draggable
              onDragStart={e => startFolderDrag(e, g)}
              onDragEnd={() => {
                setFolderDrag(null)
                setFolderDrop(null)
              }}
              $drop={folderDrop === g.id}
              $selected={selection.isSelected(g.id)}
              onClick={() => onOpenFolder?.(g.id)}
              {...folderDropProps(g)}>
              <td>
                <TableCheckbox
                  checked={selection.isSelected(g.id)}
                  onClick={e => e.stopPropagation()}
                  onChange={() => selection.toggle(g.id)}
                  inputProps={{ 'data-testid': 'folder-select', 'aria-label': `select folder ${g.name}` }}
                />
              </td>
              <td className="handle" />
              <td title={g.name}>
                <FolderName data-testid="folder-row-name">
                  <FolderOutlinedIcon sx={{ fontSize: 18, color: `${colors.primary}BF` }} />
                  <span>{g.name}</span>
                </FolderName>
              </td>
              <td>
                <MutedText style={{ fontSize: 14 }} data-testid="folder-row-kind">
                  Folder
                </MutedText>
              </td>
              <td className="num" title="Not applicable" aria-label="Not applicable">
                —
              </td>
              <td className="num">
                <MutedText style={{ fontSize: 14 }} data-testid="folder-row-items">
                  {plural(g.totalFileCount ?? 0, 'item')}
                </MutedText>
              </td>
              <td>
                <div style={{ lineHeight: 1.2 }} data-testid="folder-row-updated">
                  {g.updatedAt ? formatDay(g.updatedAt) : '—'}
                </div>
                <MutedText style={{ fontSize: 11, lineHeight: 1.2 }}>updated</MutedText>
              </td>
              <td />
              <td>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                  <IconOnlyButton
                    onClick={e => {
                      e.stopPropagation()
                      setFolderMenu({ el: e.currentTarget, folder: g })
                    }}
                    data-testid="folder-row-menu"
                    title="More"
                    style={{ color: colors.text.muted }}>
                    <MoreVertIcon sx={{ fontSize: 18 }} />
                  </IconOnlyButton>
                </div>
              </td>
            </FolderTr>
          ))}
          {files.map((f, i) => {
            const selected = selection.isSelected(f.id)
            const st = statusOf(f, running[f.id])
            const editing = renaming === f.id
            const pill = <StatusPill compact status={st.status} label={st.label} data-testid="file-status" data-status={st.status} />
            return (
              <Tr
                key={f.id}
                data-testid="file-row"
                data-file-name={f.name}
                data-file-id={f.id}
                data-selected={selected}
                data-drop={dropOf(f)}
                $selected={selected}
                $editing={editing}
                $hovered={hoveredFileId === f.id}
                $suppressHover={hoveredFileId !== null && hoveredFileId !== f.id}
                $lifted={!!drag && drag.ids.includes(f.id)}
                $line={lineOf(i)}
                draggable={!editing}
                onDragStart={e => startDrag(e, f)}
                onDragEnd={endDrag}
                onMouseMove={() => setHoveredFileId(f.id)}
                onMouseLeave={() => setHoveredFileId(id => (id === f.id ? null : id))}
                onDragOver={e => dragOverRow(e, f)}
                onDragEnter={e => dragOverRow(e, f)}
                onDrop={e => dropOnRow(e, f)}>
                <td>
                  <TableCheckbox
                    checked={selected}
                    onChange={() => selection.toggle(f.id)}
                    inputProps={{ 'data-testid': 'file-select', 'aria-label': `select ${f.name}` }}
                  />
                </td>
                <td className="handle">
                  {reorderable && (
                    <Handle data-testid="drag-handle" title="Drag to reorder, or onto a folder">
                      <DragIndicatorIcon sx={{ fontSize: 18 }} />
                    </Handle>
                  )}
                </td>
                {editing ? (
                  <td colSpan={editorSpan} style={{ whiteSpace: 'normal' }}>
                    <RenameEditor
                      file={f}
                      onSave={async name => {
                        await onRename(f, name)
                        setRenaming(null)
                      }}
                      onCancel={() => setRenaming(null)}
                    />
                  </td>
                ) : (
                  <>
                    <td title={f.name}>
                      <FileName data-testid="file-name" $link={pdfLike(f)} onClick={() => pdfLike(f) && onOpen(f)}>
                        <Name name={f.name} />
                      </FileName>
                    </td>
                    <td>
                      <KindBadge
                        kind={displayKind(f)}
                        title={f.pdfObjectKey && f.kind !== 'PDF' && f.kind !== 'COMBINED' ? `Original format: ${f.kind}` : undefined}
                        data-testid="file-kind"
                      />
                    </td>
                    <td className="num" data-testid="file-pages">
                      {f.pageCount ?? '—'}
                    </td>
                    <td className="num" data-testid="file-size">
                      {formatSize(f.sizeBytes)}
                    </td>
                    <td>
                      <div style={{ lineHeight: 1.2 }} data-testid="file-date">
                        {formatDay(f.effectiveDate)}
                      </div>
                      <MutedText style={{ fontSize: 11, lineHeight: 1.2 }} data-testid="file-date-source">
                        {f.effectiveDate ? TIER_LABEL[f.effectiveDateSource] || 'uploaded' : ''}
                      </MutedText>
                    </td>
                  </>
                )}
                <td>
                  {st.opens === 'editor' && (
                    <PillButton type="button" onClick={() => onOpen(f)} title="Open in the editor" data-testid="open-suggested">
                      {pill}
                    </PillButton>
                  )}
                  {st.opens === 'redacted' && (
                    <PillButton type="button" onClick={() => onOpen(f, 'redacted')} title="View the redacted PDF" data-testid="open-redacted">
                      {pill}
                    </PillButton>
                  )}
                  {!st.opens && pill}
                  {st.note && (
                    <MutedText style={{ fontSize: 11, lineHeight: 1.2, marginTop: 2 }} data-testid="file-status-note">
                      {st.note}
                    </MutedText>
                  )}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                    <SecondaryIconButton
                      type="button"
                      style={SquareButton}
                      onClick={() => setRenaming(f.id)}
                      disabled={editing}
                      data-testid="rename-file"
                      title="Rename">
                      <EditOutlinedIcon sx={{ fontSize: 16 }} />
                    </SecondaryIconButton>
                    <IconOnlyButton
                      onClick={e => setMenu({ el: e.currentTarget, file: f })}
                      disabled={editing}
                      data-testid="file-menu"
                      title="More"
                      style={{ color: colors.text.muted, opacity: editing ? 0.4 : 1 }}>
                      <MoreVertIcon sx={{ fontSize: 18 }} />
                    </IconOnlyButton>
                  </div>
                </td>
              </Tr>
            )
          })}
        </tbody>
      </Table>
      {(files.length > 0 || folders.length > 0) && (
        <Footer
          data-testid="table-footer"
          data-order={orderManual ? 'manual' : 'effective'}
          data-view={sort ? `${sort.key}:${sort.dir}` : hiddenCount ? 'filtered' : 'container'}>
          {countLabel ? `${countLabel}${files.length ? ' · ' : ''}` : ''}

          {files.length === 0
            ? ''
            : sort
              ? `view sorted by ${sortLabel(sort)} ${sort.dir === 'asc' ? '↑' : '↓'}. Drag-to-reorder is off while a column sort is active · default order: ${baseOrder}`
              : hiddenCount
                ? `a filter hides ${plural(hiddenCount, 'file')}. Drag-to-reorder is off while a filter narrows the list · order: ${baseOrder}`
                : orderManual
                  ? `order: manual (${containerLabel})`
                  : `order: document date (${orderCaption(priority)}), then name · dragging a row switches ${containerLabel} to manual order`}
        </Footer>
      )}
      {drag && (
        <Ghost data-testid="drag-ghost" style={{ left: drag.x + 14, top: drag.y + 12 }}>
          <DragIndicatorIcon />
          <span>{drag.name}</span>
          {drag.ids.length > 1 && <GhostCount data-testid="drag-ghost-count">+{drag.ids.length - 1}</GhostCount>}
        </Ghost>
      )}
      <ActionMenu anchorEl={menu?.el} open={!!menu} onClose={closeMenus}>
        {menu && pdfLike(menu.file) && (
          <MenuItem onClick={act(onOpen)} data-testid="open-file">
            <OpenInNewIcon /> Open
          </MenuItem>
        )}
        {menu?.file?.kind === 'COMBINED' && (
          <MenuItem onClick={act(onUncombine)} data-testid="uncombine-file">
            <CallSplitOutlinedIcon /> Uncombine…
          </MenuItem>
        )}
        {menu?.file.artifacts?.annotated && (
          <MenuItem onClick={act(f => onOpen(f, 'annotated'))} data-testid="view-annotated">
            <VisibilityOutlinedIcon /> View annotated PDF
          </MenuItem>
        )}
        {menu?.file.artifacts?.redacted && (
          <MenuItem onClick={act(f => onOpen(f, 'redacted'))} data-testid="view-redacted">
            <VisibilityOutlinedIcon /> View redacted PDF
          </MenuItem>
        )}
        <MenuItem onClick={act(f => setRenaming(f.id))} data-testid="menu-rename">
          <EditOutlinedIcon /> Rename
        </MenuItem>
        <MenuItem onClick={act(onMove)} data-testid="move-file">
          <DriveFileMoveOutlinedIcon /> Move to folder…
        </MenuItem>
        {menu && convertible(menu.file) && (
          <MenuItem onClick={act(onConvert)} data-testid="convert-file">
            <PictureAsPdfOutlinedIcon /> Convert
          </MenuItem>
        )}
        <Divider />
        {downloadMenu}
        {menu && pdfLike(menu.file) && menu.file.redactionCount > 0 && (
          <MenuItem onClick={act(onExportAnnotated)} data-testid="export-annotated">
            <DownloadOutlinedIcon /> Export annotated PDF
          </MenuItem>
        )}
        {menu && pdfLike(menu.file) && (
          <MenuItem onClick={act(onImportAnnotated)} data-testid="import-annotated">
            <UploadFileOutlinedIcon /> Replace from annotated PDF…
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={act(onExclude)} data-testid="exclude-file">
          <VisibilityOffOutlinedIcon /> Exclude…
        </MenuItem>
        <MenuItem onClick={act(onDelete)} data-testid="delete-file" className="danger">
          <DeleteOutlineIcon /> {menu?.file?.kind === 'COMBINED' ? 'Delete combined and source files permanently…' : 'Delete permanently…'}
        </MenuItem>
      </ActionMenu>
      <ActionMenu anchorEl={folderMenu?.el} open={!!folderMenu} onClose={() => setFolderMenu(null)}>
        <MenuItem onClick={folderAct(g => onOpenFolder?.(g.id))} data-testid="folder-row-open">
          <FolderOpenOutlinedIcon /> Open
        </MenuItem>
        <MenuItem onClick={folderAct(g => onRenameFolder?.(g))} data-testid="folder-row-rename">
          <EditOutlinedIcon /> Rename
        </MenuItem>
        <MenuItem onClick={folderAct(g => onMoveFolder?.(g))} data-testid="folder-row-move">
          <DriveFileMoveOutlinedIcon /> Move to folder…
        </MenuItem>
        <Divider />
        <MenuItem onClick={folderAct(g => onDeleteFolder?.(g))} data-testid="folder-row-delete" className="danger">
          <DeleteOutlineIcon /> Delete…
        </MenuItem>
      </ActionMenu>
    </>
  )
}
